// Admin → Avatars: the curated profile avatar library users can pick from.
// Avatars are database records; users reference them by id, so edits (name,
// category, image) reach everyone using the avatar. Deleting an avatar clears
// it from those users — they fall back to their uploaded photo or the default.
//
// Each avatar has a category (admin-managed: "what kind of avatar is this?")
// and an unlock type (FREE / ACHIEVEMENT / SEASONAL: "how is it obtained?").
import { useCallback, useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import Modal from '../../components/Modal/Modal';
import AvatarCropModal from '../../components/AvatarCropModal/AvatarCropModal';
import styles from './AdminAvatars.module.css';

const FALLBACK_UPLOAD = { maxFileSizeBytes: 5 * 1024 * 1024, minDimensionPx: 512, allowedMimeTypes: ['image/png', 'image/jpeg', 'image/webp'] };
const FALLBACK_UNLOCK = { types: ['FREE', 'ACHIEVEMENT', 'SEASONAL'], achievementRules: ['COIN_COUNT', 'LEGENDARY_COUNT', 'COLLECTION_COMPLETED'], collections: [] };
const EMPTY_FORM = {
    name: '', category_id: '', alt_text: '', sort_order: '', is_active: true,
    unlock_type: 'FREE', unlock_kind: 'LEGENDARY_COUNT', unlock_min: '1', unlock_collection_id: '', unlock_deadline: '', unlock_description: '',
};

const UNLOCK_TYPE_LABELS = { FREE: 'Free', ACHIEVEMENT: 'Achievement', SEASONAL: 'Seasonal' };
const UNLOCK_TYPE_HINTS = {
    FREE: 'Anyone can choose it.',
    ACHIEVEMENT: 'Shown to everyone, locked until the user earns it.',
    SEASONAL: 'Locked until the user completes a collection before a deadline.',
};
const ACHIEVEMENT_LABELS = {
    COIN_COUNT: 'Earn a number of coins',
    LEGENDARY_COUNT: 'Earn a number of Legendary coins',
    COLLECTION_COMPLETED: 'Complete a collection',
};

// ISO timestamp → value for <input type="date"> (UTC day).
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

function formFromAvatar(avatar) {
    const rule = avatar.unlockRule || {};
    return {
        ...EMPTY_FORM,
        name: avatar.name,
        category_id: String(avatar.categoryId ?? ''),
        alt_text: avatar.altText || '',
        sort_order: String(avatar.sortOrder),
        is_active: avatar.isActive,
        unlock_type: avatar.unlockType || 'FREE',
        unlock_kind: avatar.unlockType === 'ACHIEVEMENT' ? rule.kind : EMPTY_FORM.unlock_kind,
        unlock_min: rule.min ? String(rule.min) : EMPTY_FORM.unlock_min,
        unlock_collection_id: rule.collectionId || '',
        unlock_deadline: toDateInput(rule.deadline),
        unlock_description: avatar.unlockDescription || '',
    };
}

// Only the fields that apply to the chosen unlock type are sent.
function unlockFields(form) {
    const out = { unlock_type: form.unlock_type, unlock_description: form.unlock_type === 'FREE' ? '' : form.unlock_description.trim() };
    if (form.unlock_type === 'ACHIEVEMENT') {
        out.unlock_kind = form.unlock_kind;
        if (form.unlock_kind === 'COLLECTION_COMPLETED') out.unlock_collection_id = form.unlock_collection_id;
        else out.unlock_min = form.unlock_min;
    } else if (form.unlock_type === 'SEASONAL') {
        out.unlock_collection_id = form.unlock_collection_id;
        // End of the chosen day (UTC), so "by Oct 31" includes Oct 31.
        if (form.unlock_deadline) out.unlock_deadline = `${form.unlock_deadline}T23:59:59.999Z`;
    }
    return out;
}

// The picked file is only the crop source; the cropped square is what's uploaded
// (and must meet the backend's size rules, which stay authoritative).
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

function checkSource(file, upload) {
    if (!upload.allowedMimeTypes.includes(file.type)) return 'Use a PNG, JPG or WEBP image.';
    if (file.size > MAX_SOURCE_BYTES) return 'That image is too large to edit (max 25 MB).';
    return null;
}

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function AvatarForm({ avatar, categories, upload, unlock, onCancel, onSaved }) {
    const isEdit = Boolean(avatar);
    const [form, setForm] = useState(() => (avatar ? formFromAvatar(avatar) : EMPTY_FORM));
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(avatar?.imageUrl || null);
    const [cropSource, setCropSource] = useState(null); // object URL of the picked original
    const [cropping, setCropping] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => () => { if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);
    useEffect(() => () => { if (cropSource) URL.revokeObjectURL(cropSource); }, [cropSource]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

    // Picking a file opens the crop / zoom step; the crop becomes the avatar image.
    const handleFile = (e) => {
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked) return;
        const problem = checkSource(picked, upload);
        if (problem) { setError(problem); return; }
        setError(null);
        setCropSource(URL.createObjectURL(picked));
        setCropping(true);
    };

    const handleCropped = (cropped) => {
        setFile(cropped);
        setPreview(URL.createObjectURL(cropped));
        setCropping(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setError('Name is required.');
        if (!form.category_id) return setError('Choose a category.');
        if (!isEdit && !file) return setError('An image is required.');
        const needsCollection = form.unlock_type === 'SEASONAL'
            || (form.unlock_type === 'ACHIEVEMENT' && form.unlock_kind === 'COLLECTION_COMPLETED');
        if (needsCollection && !form.unlock_collection_id) return setError('Choose the collection that unlocks this avatar.');
        setSaving(true);
        setError(null);
        try {
            const fields = {
                name: form.name.trim(),
                category_id: form.category_id,
                alt_text: form.alt_text.trim(),
                is_active: form.is_active,
                ...(form.sort_order !== '' ? { sort_order: form.sort_order } : {}),
                ...unlockFields(form),
            };
            const saved = isEdit
                ? await adminApi.updateProfileAvatar(avatar.id, fields, file)
                : await adminApi.createProfileAvatar(fields, file);
            onSaved(saved, isEdit);
        } catch (err) {
            setError(err.message || 'Could not save the avatar.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit} aria-label={isEdit ? 'Edit avatar' : 'New avatar'}>
            <h2 className={styles.formTitle}>{isEdit ? `Edit “${avatar.name}”` : 'New avatar'}</h2>

            <div className={styles.formGrid}>
                <div className={styles.previewCol}>
                    <div className={styles.previewCircle}>
                        {preview ? <img src={preview} alt="Avatar preview" /> : <span>No image</span>}
                    </div>
                    <label className={styles.fileBtn}>
                        {preview ? 'Replace image' : 'Choose image'}
                        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} data-testid="avatar-image-input" />
                    </label>
                    {cropSource && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => setCropping(true)}>
                            Re-crop
                        </button>
                    )}
                    <small className={styles.hint}>
                        PNG/JPG/WEBP. You'll crop and zoom to a square; the crop needs at least {upload.minDimensionPx} px of the original.
                        Zoom out to add space around an icon (kept transparent).
                        {isEdit && file && ' Saving replaces the image for every user using this avatar.'}
                    </small>
                </div>

                <div className={styles.fields}>
                    <label>Name *
                        <input value={form.name} onChange={set('name')} maxLength={80} placeholder="e.g. Sci-Fi Explorer" />
                    </label>
                    <label>Category *
                        <select value={form.category_id} onChange={set('category_id')} aria-label="Category">
                            <option value="">Select a category…</option>
                            {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.label}</option>)}
                        </select>
                    </label>
                    <label>Alt text <span className={styles.optional}>(recommended)</span>
                        <input value={form.alt_text} onChange={set('alt_text')} maxLength={255} placeholder="Describe the image" />
                    </label>
                    <label>Sort order
                        <input type="number" min="0" step="1" value={form.sort_order} onChange={set('sort_order')} placeholder="Auto (end of category)" />
                    </label>
                    <label className={styles.checkboxRow}>
                        <input type="checkbox" checked={form.is_active} onChange={set('is_active')} />
                        Active (shown in the picker)
                    </label>

                    <fieldset className={styles.unlockFieldset}>
                        <legend>Unlock</legend>
                        <label>How is it obtained?
                            <select value={form.unlock_type} onChange={set('unlock_type')} aria-label="Unlock type">
                                {unlock.types.map((t) => <option key={t} value={t}>{UNLOCK_TYPE_LABELS[t] || t}</option>)}
                            </select>
                        </label>
                        <small className={styles.hint}>{UNLOCK_TYPE_HINTS[form.unlock_type]}</small>

                        {form.unlock_type === 'ACHIEVEMENT' && (
                            <>
                                <label>Requirement
                                    <select value={form.unlock_kind} onChange={set('unlock_kind')} aria-label="Achievement">
                                        {unlock.achievementRules.map((k) => <option key={k} value={k}>{ACHIEVEMENT_LABELS[k] || k}</option>)}
                                    </select>
                                </label>
                                {form.unlock_kind === 'COLLECTION_COMPLETED' ? (
                                    <CollectionSelect collections={unlock.collections} value={form.unlock_collection_id} onChange={set('unlock_collection_id')} />
                                ) : (
                                    <label>How many?
                                        <input type="number" min="1" step="1" value={form.unlock_min} onChange={set('unlock_min')} aria-label="Required count" />
                                    </label>
                                )}
                            </>
                        )}

                        {form.unlock_type === 'SEASONAL' && (
                            <>
                                <CollectionSelect collections={unlock.collections} value={form.unlock_collection_id} onChange={set('unlock_collection_id')} seasonalFirst />
                                <label>Deadline <span className={styles.optional}>(defaults to the collection's season end)</span>
                                    <input type="date" value={form.unlock_deadline} onChange={set('unlock_deadline')} aria-label="Deadline" />
                                </label>
                            </>
                        )}

                        {form.unlock_type !== 'FREE' && (
                            <label>Requirement text <span className={styles.optional}>(optional — generated if empty)</span>
                                <input
                                    value={form.unlock_description}
                                    onChange={set('unlock_description')}
                                    maxLength={255}
                                    placeholder="e.g. Earn your first Legendary coin"
                                    aria-label="Requirement text"
                                />
                            </label>
                        )}
                    </fieldset>
                </div>
            </div>

            {error && <div className={styles.errorBanner} role="alert">{error}</div>}

            {cropping && cropSource && (
                <AvatarCropModal
                    key={cropSource}
                    imageSrc={cropSource}
                    title="Crop avatar"
                    saveLabel="Use this crop"
                    minZoom={0.5}
                    output={{
                        format: 'image/png',
                        size: upload.minDimensionPx,
                        maxSize: 1024,
                        minSourcePx: upload.minDimensionPx,
                        maxBytes: upload.maxFileSizeBytes,
                    }}
                    onCancel={() => setCropping(false)}
                    onSave={handleCropped}
                />
            )}

            <div className={styles.formActions}>
                <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={saving}>Cancel</button>
                <button type="submit" className={styles.primaryBtn} disabled={saving}>
                    {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create avatar'}
                </button>
            </div>
        </form>
    );
}

function CollectionSelect({ collections, value, onChange, seasonalFirst = false }) {
    const sorted = seasonalFirst
        ? [...collections].sort((a, b) => Number(b.isSeasonal) - Number(a.isSeasonal) || a.name.localeCompare(b.name))
        : collections;
    return (
        <label>Collection
            <select value={value} onChange={onChange} aria-label="Collection">
                <option value="">Select a collection…</option>
                {sorted.map((c) => (
                    <option key={c.id} value={c.id}>
                        {c.name}{c.isSeasonal && c.availableUntil ? ` (ends ${new Date(c.availableUntil).toLocaleDateString()})` : ''}
                    </option>
                ))}
            </select>
        </label>
    );
}

// Admin-managed, user-facing categories: add, rename, reorder, delete (only when empty).
function CategoryManager({ categories, onChanged, onClose }) {
    const [newName, setNewName] = useState('');
    const [drafts, setDrafts] = useState({}); // id → { name, sortOrder }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const draft = (c) => drafts[c.id] ?? { name: c.label, sortOrder: String(c.sortOrder) };
    const setDraft = (c, patch) => setDrafts((d) => ({ ...d, [c.id]: { ...draft(c), ...patch } }));

    const run = async (fn) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
            await onChanged();
        } catch (err) {
            setError(err.message || 'Something went wrong.');
        } finally {
            setBusy(false);
        }
    };

    const add = (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        run(async () => {
            await adminApi.createProfileAvatarCategory({ name: newName.trim() });
            setNewName('');
        });
    };

    const save = (c) => run(async () => {
        const d = draft(c);
        await adminApi.updateProfileAvatarCategory(c.id, { name: d.name.trim(), sort_order: d.sortOrder });
        setDrafts((all) => { const next = { ...all }; delete next[c.id]; return next; });
    });

    const remove = (c) => {
        if (!window.confirm(`Delete the “${c.label}” category?`)) return;
        run(() => adminApi.deleteProfileAvatarCategory(c.id));
    };

    return (
        <section className={styles.form} aria-label="Categories">
            <div className={styles.categoryHeader}>
                <h2 className={styles.formTitle}>Categories</h2>
                <button type="button" className={styles.secondaryBtn} onClick={onClose}>Done</button>
            </div>
            <p className={styles.hint}>
                Users see these as tabs and section headings, in sort order. Only categories with active avatars appear.
                A category can be deleted once no avatars use it.
            </p>
            {error && <div className={styles.errorBanner} role="alert">{error}</div>}
            <table className={styles.table}>
                <thead>
                    <tr><th>Name</th><th>Sort order</th><th>Avatars</th><th>Actions</th></tr>
                </thead>
                <tbody>
                    {categories.map((c) => {
                        const d = draft(c);
                        const dirty = d.name !== c.label || d.sortOrder !== String(c.sortOrder);
                        return (
                            <tr key={c.id}>
                                <td>
                                    <input className={styles.inlineInput} value={d.name} maxLength={60}
                                        onChange={(e) => setDraft(c, { name: e.target.value })} aria-label={`Name of ${c.label}`} />
                                </td>
                                <td>
                                    <input className={styles.inlineInput} type="number" min="0" step="1" value={d.sortOrder}
                                        onChange={(e) => setDraft(c, { sortOrder: e.target.value })} aria-label={`Sort order of ${c.label}`} />
                                </td>
                                <td>{c.avatarCount}</td>
                                <td className={styles.rowActions}>
                                    <button type="button" onClick={() => save(c)} disabled={busy || !dirty}>Save</button>
                                    <button
                                        type="button"
                                        className={styles.dangerLink}
                                        onClick={() => remove(c)}
                                        disabled={busy || c.avatarCount > 0}
                                        title={c.avatarCount > 0 ? 'Move or delete its avatars first' : 'Delete category'}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <form className={styles.addCategory} onSubmit={add}>
                <input className={styles.inlineInput} value={newName} onChange={(e) => setNewName(e.target.value)}
                    maxLength={60} placeholder="New category, e.g. Western" aria-label="New category name" />
                <button type="submit" className={styles.primaryBtn} disabled={busy || !newName.trim()}>Add category</button>
            </form>
        </section>
    );
}

function DeleteAvatarDialog({ avatar, onCancel, onDeleted }) {
    const [usageCount, setUsageCount] = useState(avatar.usageCount);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);

    // Refresh the count so the warning reflects current usage.
    useEffect(() => {
        let cancelled = false;
        adminApi.getProfileAvatar(avatar.id)
            .then((fresh) => { if (!cancelled && fresh) setUsageCount(fresh.usageCount); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [avatar.id]);

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            const result = await adminApi.deleteProfileAvatar(avatar.id);
            onDeleted(avatar, result);
        } catch (err) {
            setError(err.message || 'Could not delete the avatar.');
            setDeleting(false);
        }
    };

    return (
        <Modal isOpen onClose={deleting ? () => {} : onCancel}>
            <div className={styles.dialog} role="alertdialog" aria-labelledby="delete-avatar-title">
                <h3 id="delete-avatar-title">Delete avatar?</h3>
                <p>
                    “{avatar.name}” is currently used by {usageCount} {usageCount === 1 ? 'user' : 'users'}.
                </p>
                <p>
                    Deleting it will remove this avatar from those users. They will fall back to their uploaded photo or default avatar.
                </p>
                {!avatar.isActive || usageCount === 0 ? null : (
                    <p className={styles.hint}>To retire it without affecting current users, disable it instead.</p>
                )}
                {error && <div className={styles.errorBanner} role="alert">{error}</div>}
                <div className={styles.formActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={deleting}>CANCEL</button>
                    <button type="button" className={styles.dangerBtn} onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'DELETE AVATAR'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default function AdminAvatars() {
    const [avatars, setAvatars] = useState([]);
    const [categories, setCategories] = useState([]);
    const [upload, setUpload] = useState(FALLBACK_UPLOAD);
    const [unlock, setUnlock] = useState(FALLBACK_UNLOCK);
    const [managingCategories, setManagingCategories] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [filter, setFilter] = useState('ALL');
    const [editing, setEditing] = useState(null); // null | 'new' | avatar
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await adminApi.listProfileAvatars();
            setAvatars(data.avatars || []);
            setCategories(data.categories || []);
            if (data.upload) setUpload(data.upload);
            if (data.unlock) setUnlock({ ...FALLBACK_UNLOCK, ...data.unlock });
        } catch (err) {
            setError(err.message || 'Failed to load avatars.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSaved = (saved, isEdit) => {
        setEditing(null);
        setMessage(isEdit ? `Saved “${saved.name}”.` : `Created “${saved.name}”.`);
        load();
    };

    const handleDeleted = (avatar, result) => {
        setDeleting(null);
        const n = result?.affectedUsers ?? 0;
        setMessage(`Deleted “${avatar.name}”.${n ? ` ${n} ${n === 1 ? 'user' : 'users'} fell back to their photo or the default avatar.` : ''}`);
        setAvatars((list) => list.filter((a) => a.id !== avatar.id));
    };

    const toggleActive = async (avatar) => {
        setError(null);
        try {
            const saved = await adminApi.updateProfileAvatar(avatar.id, { is_active: !avatar.isActive });
            setAvatars((list) => list.map((a) => (a.id === avatar.id ? saved : a)));
        } catch (err) {
            setError(err.message || 'Could not update the avatar.');
        }
    };

    const labelFor = (value) => categories.find((c) => c.value === value)?.label || value;
    const visible = filter === 'ALL' ? avatars : avatars.filter((a) => a.category === filter);

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.pageTitle}>🧑‍🎤 Avatars</h1>
                    <p className={styles.subtitle}>
                        Curated profile pictures users can choose. Disable to retire an avatar for new picks (current users keep it);
                        delete to remove it from everyone.
                    </p>
                </div>
                {editing === null && (
                    <div className={styles.headerActions}>
                        <button type="button" className={styles.secondaryBtn} onClick={() => setManagingCategories((v) => !v)}>
                            Manage categories
                        </button>
                        <button type="button" className={styles.primaryBtn} onClick={() => { setMessage(null); setEditing('new'); }}>
                            + New avatar
                        </button>
                    </div>
                )}
            </div>

            {message && <div className={styles.message}>{message}</div>}
            {error && <div className={styles.errorBanner} role="alert">{error}</div>}

            {managingCategories && editing === null && (
                <CategoryManager categories={categories} onChanged={load} onClose={() => setManagingCategories(false)} />
            )}

            {editing !== null && (
                <AvatarForm
                    key={editing === 'new' ? 'new' : editing.id}
                    avatar={editing === 'new' ? null : editing}
                    categories={categories}
                    upload={upload}
                    unlock={unlock}
                    onCancel={() => setEditing(null)}
                    onSaved={handleSaved}
                />
            )}

            <div className={styles.filters} role="tablist" aria-label="Filter by category">
                {[{ value: 'ALL', label: 'All' }, ...categories.filter((c) => c.avatarCount > 0)].map((c) => (
                    <button
                        key={c.value}
                        type="button"
                        role="tab"
                        aria-selected={filter === c.value}
                        className={`${styles.filterPill} ${filter === c.value ? styles.filterPillActive : ''}`}
                        onClick={() => setFilter(c.value)}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className={styles.empty}>Loading avatars…</div>
            ) : visible.length === 0 ? (
                <div className={styles.empty}>No avatars yet. Create one to start the library.</div>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Preview</th>
                                <th>Name</th>
                                <th>Category</th>
                                <th>Unlock</th>
                                <th>Active</th>
                                <th>Sort order</th>
                                <th>Users using avatar</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((a) => (
                                <tr key={a.id} className={a.isActive ? '' : styles.inactiveRow}>
                                    <td><img className={styles.thumb} src={a.imageUrl} alt={a.altText || a.name} /></td>
                                    <td>{a.name}</td>
                                    <td>{a.categoryLabel || labelFor(a.category)}</td>
                                    <td>
                                        <span className={styles.unlockBadge}>{UNLOCK_TYPE_LABELS[a.unlockType] || a.unlockType}</span>
                                        {a.requirement && <div className={styles.hint}>{a.requirement}</div>}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className={`${styles.statusPill} ${a.isActive ? styles.statusOn : styles.statusOff}`}
                                            onClick={() => toggleActive(a)}
                                            title={a.isActive ? 'Disable (hide from picker)' : 'Enable (show in picker)'}
                                        >
                                            {a.isActive ? 'Active' : 'Inactive'}
                                        </button>
                                    </td>
                                    <td>{a.sortOrder}</td>
                                    <td title="Users currently using this avatar">{a.usageCount}</td>
                                    <td>{formatDate(a.createdAt)}</td>
                                    <td className={styles.rowActions}>
                                        <button type="button" onClick={() => { setMessage(null); setEditing(a); }}>Edit</button>
                                        <button type="button" className={styles.dangerLink} onClick={() => setDeleting(a)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {deleting && (
                <DeleteAvatarDialog avatar={deleting} onCancel={() => setDeleting(null)} onDeleted={handleDeleted} />
            )}
        </div>
    );
}
